import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Mail, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { SEOHead, breadcrumbSchema } from '../components/seo/SEOHead';
import { downloadQRCodePNG, downloadQRCodeSVG } from '../utils/qrHelpers';
import { trackEvent } from '../components/analytics/GoogleAnalytics';

export const EmailQRPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [size, setSize] = useState(300);

  const qrContent = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const isValid = email.includes('@') && email.includes('.');

  const breadcrumbs = breadcrumbSchema([
    { name: 'Home', url: 'https://freeqrcodenow.com/' },
    { name: 'Email QR Code Generator', url: 'https://freeqrcodenow.com/email-qr-code-generator' },
  ]);

  return (
    <>
      <SEOHead
        title="Free Email QR Code Generator | Create Email QR Codes Instantly"
        description="Generate free email QR codes for instant email communication. Create QR codes with pre-filled email, subject, and body. Download as PNG or SVG."
        keywords="email qr code, mailto qr code, email qr generator, free email qr code, contact qr code"
        structuredData={breadcrumbs}
      />

      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            <Mail className="inline h-10 w-10 mr-3 text-blue-600" />
            Free Email QR Code Generator
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Create QR codes for instant email contact. Pre-fill email addresses, subjects, and messages!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          {/* Input Section */}
          <Card>
            <CardHeader>
              <CardTitle>Email Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                label="Email Address *"
                placeholder="contact@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                error={email && !isValid ? 'Invalid email address' : undefined}
              />

              <Input
                label="Subject (Optional)"
                placeholder="Email subject line"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />

              <div>
                <label className="block text-sm font-medium mb-2">Body (Optional)</label>
                <textarea
                  placeholder="Pre-filled email message..."
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  className="w-full p-2 border border-input rounded-md bg-background min-h-[120px]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">QR Code Size: {size}px</label>
                <input
                  type="range"
                  min="200"
                  max="500"
                  value={size}
                  onChange={(e) => setSize(parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
            </CardContent>
          </Card>

          {/* Preview Section */}
          <Card>
            <CardHeader>
              <CardTitle>QR Code Preview</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center space-y-6">
              {isValid ? (
                <>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <QRCodeSVG
                      value={qrContent}
                      size={size}
                      level="M"
                      includeMargin={true}
                    />
                  </div>

                  <div className="flex flex-wrap gap-3 justify-center">
                    <Button onClick={() => {
                      downloadQRCodePNG(qrContent, { size, foregroundColor: '#000000', backgroundColor: '#ffffff', errorCorrectionLevel: 'M', marginSize: 4 }, false, 'email-qr-code.png');
                      trackEvent('download', 'Email QR', 'PNG');
                    }}>
                      <Download className="h-4 w-4 mr-2" />
                      Download PNG
                    </Button>
                    <Button variant="outline" onClick={() => {
                      downloadQRCodeSVG(qrContent, { size, foregroundColor: '#000000', backgroundColor: '#ffffff', errorCorrectionLevel: 'M', marginSize: 4 }, 'email-qr-code.svg');
                      trackEvent('download', 'Email QR', 'SVG');
                    }}>
                      <Download className="h-4 w-4 mr-2" />
                      Download SVG
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <Mail className="h-16 w-16 mb-4 text-gray-300" />
                  <p>Enter email address to generate QR code</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* SEO Content */}
        <div className="mt-12 prose prose-lg max-w-none">
          <h2>Create QR Codes for Email Communication</h2>
          <p>
            Email QR codes make it incredibly easy for people to contact you. No more typing email addresses
            or subject lines – just scan and send!
          </p>

          <h2>How It Works</h2>
          <p>
            When someone scans your email QR code, their default email app opens automatically with:
          </p>
          <ul>
            <li>Your email address pre-filled in the "To:" field</li>
            <li>Subject line already entered (if specified)</li>
            <li>Email body text ready to send (if specified)</li>
          </ul>

          <h2>Perfect For:</h2>
          <ul>
            <li><strong>Customer Support:</strong> Quick access to support email</li>
            <li><strong>Marketing Materials:</strong> Easy newsletter signups</li>
            <li><strong>Business Cards:</strong> Professional contact method</li>
            <li><strong>Events:</strong> Simplified feedback collection</li>
            <li><strong>Product Packaging:</strong> Quick product registration</li>
            <li><strong>Posters & Flyers:</strong> Instant contact option</li>
          </ul>

          <h2>Tips for Success</h2>
          <ul>
            <li>Use clear, concise subject lines</li>
            <li>Keep pre-filled body text brief</li>
            <li>Test on multiple devices before printing</li>
            <li>Consider using a dedicated support or contact email</li>
            <li>Make QR codes large enough for easy scanning (minimum 2cm x 2cm)</li>
          </ul>

          <h2>Free & No Registration Required</h2>
          <p>
            Our email QR code generator is completely free. No account needed, no hidden fees.
            Generate unlimited QR codes for all your email communication needs!
          </p>
        </div>
      </div>
    </>
  );
};


